<script setup>
// Stats tab — lifetime aggregates for the signed-in user, from GET /api/me/stats
// (scoped to req.user.id server-side).
import { ref, onMounted, computed } from "vue";

const loading = ref(true);
const error = ref("");
const stats = ref(null);

const hasData = computed(() => stats.value && stats.value.answered > 0);

function fmtMs(ms) {
  if (ms == null) return "—";
  return ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms}ms`;
}

onMounted(async () => {
  try {
    const res = await fetch("/api/me/stats", { credentials: "include" });
    if (!res.ok) throw new Error(`Stats request failed (${res.status})`);
    const data = await res.json();
    stats.value = data.stats;
  } catch (e) {
    error.value = e.message || "Could not load your stats.";
  } finally {
    loading.value = false;
  }
});
</script>

<template>
  <div class="card border-0 shadow-sm">
    <div class="card-body">
      <h2 class="h5 mb-3"><i class="bi bi-bar-chart me-2"></i>Your stats</h2>

      <div v-if="loading" class="text-muted py-3">
        <span class="spinner-border spinner-border-sm me-2"></span>Crunching your numbers…
      </div>

      <div v-else-if="error" class="alert alert-warning py-2 mb-0">{{ error }}</div>

      <p v-else-if="!hasData" class="text-muted mb-0">
        No data yet. Complete a quiz from the <strong>Choose deck</strong> tab and your
        accuracy, first-try rate, and pace will show up here.
      </p>

      <div v-else class="row g-3">
        <div class="col-6 col-md-4">
          <div class="border rounded p-3 h-100">
            <div class="text-muted small">Overall accuracy</div>
            <div class="fs-3 fw-semibold">{{ stats.accuracy }}%</div>
          </div>
        </div>
        <div class="col-6 col-md-4">
          <div class="border rounded p-3 h-100">
            <div class="text-muted small">First-try accuracy</div>
            <div class="fs-3 fw-semibold">{{ stats.firstTryAccuracy }}%</div>
          </div>
        </div>
        <div class="col-6 col-md-4">
          <div class="border rounded p-3 h-100">
            <div class="text-muted small">Questions answered</div>
            <div class="fs-3 fw-semibold">{{ stats.answered }}</div>
          </div>
        </div>
        <div class="col-6 col-md-4">
          <div class="border rounded p-3 h-100">
            <div class="text-muted small">Sessions</div>
            <div class="fs-3 fw-semibold">{{ stats.sessions }}</div>
          </div>
        </div>
        <div class="col-6 col-md-4">
          <div class="border rounded p-3 h-100">
            <div class="text-muted small">Correct</div>
            <div class="fs-3 fw-semibold">{{ stats.correct }}</div>
          </div>
        </div>
        <div class="col-6 col-md-4">
          <div class="border rounded p-3 h-100">
            <div class="text-muted small">Avg response time</div>
            <div class="fs-3 fw-semibold">{{ fmtMs(stats.avgAnswerMs) }}</div>
          </div>
        </div>
      </div>

      <p v-if="hasData" class="text-muted small mb-0 mt-3">
        First-try accuracy counts questions you got right on the first attempt — the
        clearest signal of confident recall.
      </p>
    </div>
  </div>
</template>
